<template>
    <form action="#"
          class="parametric-send">
        <div v-show="step === 1"
             class="parametric-send__agency">
            <div class="parametric-send__title">
                Указать данные агенства?
            </div>

            <div class="parametric-send__agency-input">
                <STInput id="agency-name"
                         ref="agencyNameInput"
                         :value="agencyName"
                         name="agency-name"
                         label="Название агенства"
                         @input="onAgencyNameInput" />
            </div>

            <div class="parametric-send__agency-input">
                <STInput
                    id="agency-phone"
                    ref="agencyPhoneInput"
                    :value="agencyPhone"
                    :modify="{'is-error': $v.agencyPhone.$dirty && $v.agencyPhone.$invalid}"
                    :error="agencyPhoneErrorText"
                    name="agency-phone"
                    label="Номер телефона агента"
                    @input="onPhoneInput"
                    @focus="onPhoneFocus" />
            </div>

            <STDragAndDrop v-if="!notNeedsLogo"
                           title="Логотип агенства"
                           :limit="1"
                           :max-size="5"
                           accept=".jpg, .jpeg, .png"
                           description="Загрузите файл в формате, JPEG, JPG, PNG не более 5 Мб"
                           :clear-on-drop="true"
                           @updateUploadFiles="addAgencyFiles" />

            <div class="parametric-send__agency-logo">
                <STCheckbox id="logo-agreement"
                            v-model="notNeedsLogo"
                            name="logo-agreement">
                    <span>Не выводить лого</span>
                </STCheckbox>
            </div>


            <STBaseButton modify="parametric-send__agency-send button_full_width"
                          text="Продолжить"
                          :disabled="!isPhoneValid"
                          @click="nextStep" />
        </div>

        <div v-show="step === 2"
             class="parametric-send__mail">
            <div class="parametric-send__title">
                Отправить на почту
            </div>

            <div class="parametric-send__mail-input">
                <STInput id="email"
                         v-model.trim="email"
                         name="email"
                         label="Введите адрес электронной почты"
                         :error="emailErrorText"
                         :modify="{'is-error': $v.email.$dirty && $v.email.$invalid}"
                         @input="$v.email.$touch()"
                         @focus="$v.email.$touch()" />
            </div>

            <STBaseButton modify="parametric-send__mail-send button_full_width"
                          :disabled="!email"
                          text="Отправить"
                          @click="nextStep" />
        </div>

        <div v-show="step === 3"
             class="parametric-send__loader">
            <STLoader :loading="loading"
                      :has-loaded="hasLoaded"
                      :message="loaderMessage"
                      modify="loader_full_size" />
        </div>

        <div v-show="step === 4"
             class="parametric-send__result">
            <div class="parametric-send__title">
                {{ resultText }}
            </div>

            <STBaseButton modify="parametric-send__result-send button_full_width"
                          :text="resultButton"
                          @click="$emit('closePopup')" />
        </div>
    </form>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import {handlePhoneFocus, handlePhoneInput, isPhoneComplete} from '@/helpers/phone';
import {required} from 'vuelidate/lib/validators';

const MAX_AGENCY_NAME_LENGTH = 50;

/**
 * Компонент для формирования пдф
 */
export default {
    name: 'ParametricSend',

    props: {
        type : String,
        flats: Array
    },

    data() {
        return {
            loading     : false, // проиходит ли сейчас отправка
            hasLoaded   : false, // завершилась ли отправка
            step        : 1,     // текущий шаг формирования отправки
            email       : '',    // email куда отправляем
            agencyName  : '',    // название агенства для вставки в пдф
            agencyPhone : '',    // телефон агента
            agencyFile  : null,  // логотип агенства для вставки в пдф
            resultText  : '',    // текст выводимый после формирования пдф
            resultButton: '',     // текст кнопки выводимый после формирования пдф
            notNeedsLogo: false
        };
    },

    validations: {
        agencyPhone: {
            validFormat(value) {
                const digits = String(value || '').replace(/\D/g, '');

                if (!digits || digits === '7' || digits === '8') {
                    return true;
                }

                return isPhoneComplete(value);
            }
        },
        email: {
            required
        }
    },

    computed: {
        ...mapGetters({
            user: 'GET_USER'
        }),

        getSurnameFirstLetter() {
            return this.user && this.user.surname ? `${this.user.surname[0]}.` : '';
        },

        getFirstName() {
            return this.user && this.user.firstName ? this.user.firstName : '';
        },

        /**
         * Текст выводимый при формировании пдф
         * @return {string}
         */
        loaderMessage() {
            if (this.hasLoaded) {
                return 'PDF успешно сформирован';
            }

            return 'PDF формируется, подождите';
        },

        isPhoneValid() {
            const digits = this.agencyPhone.replace(/\D/g, '');

            if (!digits || digits === '7' || digits === '8') {
                return true;
            }

            return isPhoneComplete(this.agencyPhone);
        },

        agencyPhoneErrorText() {
            return 'Заполните или очистите поле';
        },

        emailErrorText() {
            return 'Это обязательное поле';
        }
    },

    methods: {
        ...mapActions('parametric', ['sendToPrint']),

        /**
         * Добавляет логотип агенства
         * @param files
         */
        addAgencyFiles(files) {
            this.agencyFile = files;
        },

        /**
         * Переход к следующему шагу
         */
        nextStep() {
            if (this.step === 1) {
                this.$v.agencyPhone.$touch();

                if (this.$v.agencyPhone.$invalid) {
                    return;
                }
            }

            if (this.step === 2) {
                this.$v.email.$touch();

                if (this.$v.email.$invalid) {
                    return;
                }
            }

            // Если пдф нужно скачать, а не отправить на email,
            // то переходим сразу к третьему шагу (шаг отправки данных и формирования пдф)
            // иначе переходим к следущему шагу (заполнении email куда оптарвляем)
            if (this.step === 1 && this.type === 'download') {
                this.step = 3;
            } else {
                this.step++;
            }

            // Если 3-й шаг, то отправляем данные на бэк
            if (this.step === 3) {
                this.sendToBackend();
            }
        },

        /**
         * Отправляет данные на бэк, для формирования пдф
         */
        sendToBackend() {
            this.loading = true;

            let formData = new FormData();

            formData.append('show_logo', !this.notNeedsLogo);

            if (this.agencyFile && !this.notNeedsLogo) {
                formData.append('logo', this.agencyFile[0]);
            }

            if (this.agencyName) {
                formData.append('agency', this.agencyName.slice(0, MAX_AGENCY_NAME_LENGTH));
            }

            if (this.email) {
                formData.append('email', this.email);
            }

            if (this.agencyPhone) {
                formData.append('phone', this.agencyPhone);
            }

            if (this.getFirstName) {
                formData.append('name', `${this.getFirstName} ${this.getSurnameFirstLetter}`);
            }

            formData.append('flats', this.flats);
            formData.append('type', this.type);

            this.sendToPrint(formData)
                .then(({data}) => {
                    if (!data?.request?.status) {
                        throw new Error('Произошла ошибка формирования PDF');
                    }

                    this.hasLoaded = true;

                    this.resultText = 'PDF файл отправлен';
                    this.resultButton = 'Вернуться в кабинет';

                    this.downloadUrl(data?.request?.pdfPath);
                })
                .catch((error) => {
                    this.resultText = 'Произошла ошибка формирования PDF';
                    this.resultButton = 'Попробовать позже';

                    console.error(error);
                })
                .finally(() => {
                    this.step = 4;
                    this.loading = false;
                });
        },

        downloadUrl(url) {
            if (!url) {
                return;
            }

            const link = document.createElement('a');

            link.setAttribute('download', '');
            link.href = url;

            document.body.appendChild(link);

            link.click();
            link.remove();
        },

        onPhoneFocus() {
            handlePhoneFocus(this, 'agencyPhone', 'agencyPhoneInput');
            this.$v.agencyPhone.$touch();
        },

        onPhoneInput(value) {
            handlePhoneInput(this, 'agencyPhone', 'agencyPhoneInput', value);
            this.$v.agencyPhone.$touch();
        },

        onAgencyNameInput(value) {
            const truncated = (value || '').slice(0, MAX_AGENCY_NAME_LENGTH);

            this.agencyName = truncated;

            const input = this.$refs.agencyNameInput && this.$refs.agencyNameInput.$refs.input;

            if (input && input.value !== truncated) {
                input.value = truncated;
            }
        },
    }
};
</script>

<style lang="scss">
@import "ParametricSend";
</style>
